import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { fetchRatings, fetchStats } from "../lib/api";

export function DashboardPage() {
	const { user } = useAuth();
	const { data: stats, isLoading: statsLoading } = useQuery({
		queryKey: ["stats"],
		queryFn: fetchStats,
	});

	const { data: ratingsData, isLoading: ratingsLoading } = useQuery({
		queryKey: ["ratings"],
		queryFn: () => fetchRatings({ limit: 5 }),
	});

	if (statsLoading || ratingsLoading) {
		return (
			<div className="flex justify-center py-12">
				<div className="font-label-bold text-on-surface uppercase animate-pulse">
					Loading...
				</div>
			</div>
		);
	}

	const hasRatings = stats && stats.total_ratings > 0;
	const recentSips = ratingsData?.data ?? [];

	return (
		<div className="px-4 md:px-0">
			<header className="mb-6 md:mb-8 flex justify-between items-end">
				<div>
					<h1 className="font-[Space_Grotesk] text-[28px] md:text-[48px] leading-[1.1] tracking-[-0.04em] font-bold text-on-surface">
						DASHBOARD
					</h1>
					<p className="font-body-lg text-[14px] md:text-[18px] leading-[1.5] font-medium text-outline mt-1">
						Welcome back,{" "}
						{user?.user_metadata?.full_name ||
							user?.email?.split("@")[0] ||
							"Caffeine Fiend"}
						.
					</p>
				</div>
				<Link
					to="/ratings/new"
					className="hidden md:flex bg-brand-green border-4 border-brand-black px-8 py-4 text-brand-black font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-2 hover:translate-y-2 hover:shadow-none transition-all items-center gap-2"
				>
					<span className="material-symbols-outlined font-black">add</span>
					LOG A DRINK
				</Link>
			</header>

			{!hasRatings ? (
				<div className="bg-brand-white border-4 border-brand-black p-8 md:p-12 text-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
					<span
						className="material-symbols-outlined text-[60px] md:text-[80px] text-brand-green mb-4 block"
						style={{ fontVariationSettings: "'FILL' 1" }}
					>
						local_cafe
					</span>
					<h2 className="font-headline-md text-[20px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] uppercase mb-2">
						No Drinks Logged Yet
					</h2>
					<p className="font-body-md text-[14px] md:text-[16px] leading-[1.5] text-outline mb-6">
						Start tracking your energy drink adventures. Rate your first one!
					</p>
					<Link
						to="/ratings/new"
						className="inline-flex md:hidden bg-brand-green border-4 border-brand-black px-8 py-4 text-brand-black font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all items-center gap-2"
					>
						<span className="material-symbols-outlined">bolt</span>
						RATE YOUR FIRST DRINK
					</Link>
				</div>
			) : (
				<div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-8">
					<div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
						<div className="bg-brand-white border-4 border-brand-black p-4 md:p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] col-span-1 sm:col-span-2 flex justify-between items-center">
							<div>
								<h2 className="font-label-bold text-[11px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] text-outline uppercase">
									Total Drinks Logged
								</h2>
								<div className="font-[Space_Grotesk] text-[32px] md:text-[48px] leading-[1.1] tracking-[-0.04em] font-bold text-brand-blue mt-1 md:mt-2">
									{stats?.total_ratings}
								</div>
							</div>
							<span
								className="material-symbols-outlined text-[40px] md:text-[60px] text-brand-blue"
								style={{ fontVariationSettings: "'FILL' 1" }}
							>
								local_cafe
							</span>
						</div>

						<div className="bg-brand-yellow border-4 border-brand-black p-4 md:p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
							<h2 className="font-label-bold text-[11px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] text-brand-black uppercase">
								Avg Flavor Score
							</h2>
							<div className="font-headline-lg text-[24px] md:text-[32px] leading-[1.2] tracking-[-0.02em] font-bold font-[Space_Grotesk] text-brand-black mt-1 md:mt-2">
								{stats?.average_flavor_score} / 5
							</div>
						</div>

						<div className="bg-brand-white border-4 border-brand-black p-4 md:p-6 shadow-[4px_4px_0px_0px_#0052FF]">
							<h2 className="font-label-bold text-[11px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] text-outline uppercase">
								Top Flavor
							</h2>
							<div className="font-headline-lg text-[20px] md:text-[32px] leading-[1.2] tracking-[-0.02em] font-bold font-[Space_Grotesk] text-brand-blue mt-1 md:mt-2 break-words">
								{stats?.top_flavor?.drink_name ?? "--"}
							</div>
						</div>
					</div>

					<div className="lg:col-span-4 bg-brand-white border-4 border-brand-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex flex-col">
						<div className="p-4 md:p-6 border-b-4 border-brand-black bg-surface-container-highest">
							<h2 className="font-headline-md text-[18px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] text-on-surface uppercase">
								Recent Sips
							</h2>
						</div>
						<div className="p-4 md:p-6 flex-grow flex flex-col gap-3 md:gap-4">
							{recentSips.length === 0 ? (
								<p className="font-body-md text-[14px] md:text-[16px] leading-[1.5] text-outline">
									No recent ratings.
								</p>
							) : (
								recentSips.map((rating) => (
									<div
										key={rating.id}
										className="flex items-center gap-3 md:gap-4 border-2 border-brand-black p-2 md:p-3 hover:bg-surface-container-high transition-colors"
									>
										<div
											className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-brand-black overflow-hidden flex items-center justify-center flex-shrink-0 ${
												rating.category === "energy"
													? "bg-brand-blue"
													: rating.category === "soda"
														? "bg-brand-orange"
														: "bg-brand-green"
											}`}
										>
											<span
												className={`material-symbols-outlined text-[20px] ${
													rating.category === "energy"
														? "text-brand-white"
														: "text-brand-black"
												}`}
											>
												{rating.category === "energy"
													? "bolt"
													: rating.category === "soda"
														? "local_drink"
														: "bubble_chart"}
											</span>
										</div>
										<div className="flex-grow min-w-0">
											<div className="font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] text-on-surface truncate">
												{rating.drink_name}
											</div>
											<div className="font-body-md text-[12px] md:text-[16px] leading-[1.5] text-outline-variant">
												{rating.brand && `${rating.brand} · `}
												{timeAgo(rating.created_at)}
											</div>
										</div>
										<div className="font-headline-md text-[20px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] text-brand-blue">
											{rating.flavor_score}
										</div>
									</div>
								))
							)}
						</div>
						<Link
							to="/ratings"
							className="w-full bg-brand-blue border-t-4 border-brand-black p-3 md:p-4 text-brand-white font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase hover:bg-brand-black transition-colors text-center"
						>
							View All Sips
						</Link>
					</div>
				</div>
			)}
		</div>
	);
}

function timeAgo(dateStr: string): string {
	const now = Date.now();
	const then = new Date(dateStr).getTime();
	const diffSec = Math.floor((now - then) / 1000);

	if (diffSec < 60) return "just now";
	const mins = Math.floor(diffSec / 60);
	if (mins < 60) return `${mins} min ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours} hr ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
	const weeks = Math.floor(days / 7);
	if (weeks < 4) return `${weeks} wk ago`;
	const months = Math.floor(days / 30);
	return `${months} mo ago`;
}
